import { useEffect, useMemo, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { handleUpdateRowFields } from '../../utils/calc-util';
import { useInputTableRows } from './hooks/useDetailRows';
import { AritoFormV2 } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { initialFormValues } from '../../schema';
import { useFormFieldState } from '../../hooks';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { FormMode } from '@/types/form';
import BottomBar from './BottomBar';

import { DetailTab } from './DetailTab';
import { useTaxRows } from './hooks';
import { TaxTab } from './TaxTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: BanHangRecord;
  onSubmit?: (data: BanHangPayload) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('info');
  const { entityUnit } = useAuth();
  const { state, actions } = useFormFieldState(initialData);
  const {
    rows: detailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange
  } = useInputTableRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields, state);
  const {
    rows: taxRows,
    selectedRowUuid: taxSelectedRowUuid,
    selectedCell: taxSelectedCell,
    handleRowClick: taxHandleRowClick,
    handleCellClick: taxHandleCellClick,
    handleAddRow: taxHandleAddRow,
    handleDeleteRow: taxHandleDeleteRow,
    handleCopyRow: taxHandleCopyRow,
    handlePasteRow: taxHandlePasteRow,
    handleMoveRow: taxHandleMoveRow,
    handleCellValueChange: taxHandleCellValueChange
  } = useTaxRows(initialData?.thue_data || []);

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
      setActiveTab('info');
    }
  }, [open]);

  const { totalPSNo, totalPSCo } = useMemo(() => {
    return calculateTotals(detailRows);
  }, [detailRows]);

  const handleSubmit = (data: BanHangPayload) => {
    const formData = transformFormData(data, state, totalPSNo, totalPSCo, detailRows, taxRows);
    onSubmit?.({ ...formData, unit_id: entityUnit?.uuid });
    setIsFormDirty(false);
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    onClose
  });

  return (
    <>
      <AritoFormV2
        mode={formMode}
        initialData={initialData || initialFormValues}
        title={title}
        actionButtons={actionButtons}
        subTitle='Chứng từ phải thu khác, chứng từ bù trừ công nợ'
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={[
          {
            id: 'info',
            label: 'Thông tin',
            component: (
              <div onChange={() => setIsFormDirty(true)}>
                <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              </div>
            )
          }
        ]}
        headerTabActions={
          formMode === 'view' && initialData?.uuid ? (
            <InfoDropdown uuid={initialData.uuid} ma_ct={initialData?.chung_tu} />
          ) : null
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: (
                <TaxTab
                  formMode={formMode}
                  rows={taxRows}
                  selectedRowUuid={taxSelectedRowUuid}
                  selectedCell={taxSelectedCell}
                  onRowClick={taxHandleRowClick}
                  onCellClick={taxHandleCellClick}
                  onAddRow={taxHandleAddRow}
                  onDeleteRow={taxHandleDeleteRow}
                  onCopyRow={taxHandleCopyRow}
                  onPasteRow={taxHandlePasteRow}
                  onMoveRow={taxHandleMoveRow}
                  onCellValueChange={taxHandleCellValueChange}
                />
              )
            }
          ]
        }
        bottomBar={activeTab === 'info' && <BottomBar totalPSNo={totalPSNo} totalPSCo={totalPSCo} state={state} />}
      />
    </>
  );
};

export default FormDialog;
